/**
 * Senior Living / Brain Health building type configuration.
 * Extracted from brainHealth/ vertical.
 */

// Points system (BHPS)
import {
  PATHWAY_CAPS, PRECONDITIONS, FEATURES, FEATURE_MAP,
  checkPreconditions, autoDetectFeatures, scoreSpace, scoreProject, getCertificationTier,
} from '../../brainHealth/engine/bhps';

// Financial engines
import {
  PATHWAY_COEFFICIENTS, ROLE_DEFAULTS, STAFF_EVIDENCE,
  calculateStaffRoi,
} from '../../brainHealth/engine/financialStaff';

import {
  ACUITY_TIERS, RESIDENT_EVIDENCE,
  calculateResidentRoiByTier, calculateResidentRoi,
} from '../../brainHealth/engine/financialResident';

export default {
  type: 'seniorLiving',
  label: 'Brain Health / Senior Living',
  shortLabel: 'Brain Health',
  color: '#3b82f6',  // Blue (Colors.primary)
  icon: 'brain',

  // --- Unit of analysis ---
  unitLabel: 'Space',
  unitLabelPlural: 'Spaces',
  unitIdField: 'spaceId',

  // --- Space types ---
  spaceTypes: [
    { id: 'dining', label: 'Dining Room', icon: 'utensils' },
    { id: 'common_area', label: 'Common Area', icon: 'users' },
    { id: 'bedroom', label: 'Resident Bedroom', icon: 'bed' },
    { id: 'corridor', label: 'Corridor', icon: 'arrows-alt-h' },
    { id: 'lobby', label: 'Lobby / Entry', icon: 'door-open' },
    { id: 'activity_room', label: 'Activity Room', icon: 'puzzle-piece' },
    { id: 'therapy', label: 'Therapy / Rehab', icon: 'heartbeat' },
    { id: 'garden', label: 'Garden / Outdoor', icon: 'leaf' },
    { id: 'chapel', label: 'Chapel / Meditation', icon: 'pray' },
    { id: 'salon', label: 'Salon / Spa', icon: 'spa' },
    { id: 'office', label: 'Staff Office', icon: 'briefcase' },
    { id: 'general', label: 'General', icon: 'building' },
  ],

  // --- Input fields (standard 8-pathway building-scale) ---
  inputFields: {
    daylight_lux: { label: 'Daylight (lux)', unit: 'lux', pathway: 'daylight' },
    noise_db: { label: 'Noise Level (dB)', unit: 'dB', pathway: 'noise' },
    pm25_ug_m3: { label: 'PM2.5 (µg/m³)', unit: 'µg/m³', pathway: 'air_quality' },
    visual_pattern_stress: { label: 'Visual Pattern Stress (0-1)', unit: '', pathway: 'visual_stress' },
    enrichment_score: { label: 'Enrichment Score (0-1)', unit: '', pathway: 'enrichment' },
    wayfinding_score: { label: 'Wayfinding Score (0-1)', unit: '', pathway: 'wayfinding' },
    movement_minutes_day: { label: 'Movement (min/day)', unit: 'min', pathway: 'movement' },
    nature_minutes_day: { label: 'Nature Exposure (min/day)', unit: 'min', pathway: 'nature' },
    nature_view: { label: 'Nature View', unit: 'boolean', pathway: 'nature' },
  },

  // --- Points system (BHPS) ---
  points: {
    name: 'BHPS',
    fullName: 'Brain Health Points Score',
    pathwayCaps: PATHWAY_CAPS,
    preconditions: PRECONDITIONS,
    features: FEATURES,
    featureMap: FEATURE_MAP,
    tiers: { platinum: 75, gold: 60, silver: 45, bronze: 30 },
    checkPreconditions,
    autoDetectFeatures,
    scoreSpace,
    scoreProject,
    getCertificationTier,
  },

  // --- Financial model ---
  financial: {
    staffLabel: 'Staff',
    occupantLabel: 'Resident',
    staffPathwayCoefficients: PATHWAY_COEFFICIENTS,
    staffRoleDefaults: ROLE_DEFAULTS,
    staffEvidence: STAFF_EVIDENCE,
    calculateStaffRoi,
    occupantAcuityTiers: ACUITY_TIERS,
    occupantEvidence: RESIDENT_EVIDENCE,
    calculateOccupantRoi: calculateResidentRoiByTier,
    calculateOccupantRoiLegacy: calculateResidentRoi,
    staffChannels: [
      { name: 'Stress Reduction', key: 'stress' },
      { name: 'Accuracy / Rework', key: 'accuracy' },
      { name: 'Productivity', key: 'productivity' },
      { name: 'Retention', key: 'retention' },
    ],
    occupantChannels: [
      { name: 'Fall Prevention', key: 'falls' },
      { name: 'Cognitive Preservation', key: 'cognitive' },
      { name: 'Mood / Depression', key: 'mood' },
      { name: 'Hospitalization', key: 'health' },
    ],
  },

  // --- Optimizer targets ---
  optimizerTargets: null,  // Uses default targets in optimizer.js

  // --- Neuroscience labels ---
  neuroscience: {
    outcomeLabels: {
      dorsal: 'Cognitive & Spatial (Dorsal Hippocampus)',
      ventral: 'Emotional & Mood (Ventral Hippocampus)',
    },
    contextDescription: 'Senior living environments impact resident cognition, mood, and independence.',
    temporalLabels: {
      immediate: 'Immediate (minutes-hours)',
      shortTerm: 'Short-term (days-weeks)',
      longTerm: 'Long-term (months-years)',
    },
  },

  // --- Route prefix ---
  routePrefix: 'brain-health',
  projectTypeKey: 'brain_health',

  // --- Demo data ---
  demos: [],
};
